import { functions } from '../../firebase';
import React from 'react';
import ReactDOM from 'react-dom';
import { useScrollLock } from '../../hooks/useScrollLock';
import { httpsCallable } from 'firebase/functions';

import { useSubscription } from '../contexts/SubscriptionContext';
import { CrownIcon, CheckCircleIcon, SparklesIcon, CloseIcon } from './Icons';

interface UpgradeModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    message?: string;
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({
    isOpen,
    onClose,
    title = "Unlock Unlimited Access",
    message = "You've reached the free limit for this feature. Upgrade to Premium for unlimited access!"
}) => {
    useScrollLock(isOpen);
    const { hasUsedTrial } = useSubscription();

    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    if (!isOpen) return null;

    const handleUpgrade = async (planType: 'monthly' | 'annual') => {
        setIsLoading(true);
        setError(null);
        try {
                        const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');

            const result = await createCheckoutSession({
                planType,
                successUrl: window.location.origin + '/app?success=true',
                cancelUrl: window.location.origin + '/app?canceled=true'
            });

            const data = result.data as { url: string };
            if (data.url) {
                window.location.href = data.url;
            } else {
                throw new Error("No checkout URL returned.");
            }
        } catch (err: any) {
            console.error("Checkout Error:", err);
            setError(err.message || "Failed to start checkout.");
            setIsLoading(false);
        }
    };

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div 
                className="absolute inset-0 bg-black/80 backdrop-blur-md transition-opacity animate-in fade-in duration-300"
            />


            {/* Modal Content */}
            <div className="relative w-full max-w-4xl bg-[#1a1f2e] text-white rounded-[32px] shadow-2xl overflow-hidden border border-white/10 flex flex-col md:flex-row animate-in fade-in zoom-in-95 duration-300">
                
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-6 right-6 z-10 p-2 rounded-full bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all hover:rotate-90"
                    disabled={isLoading}
                >
                    <CloseIcon className="w-5 h-5" />
                </button>

                {/* Left Side: Value Prop */}
                <div className="p-10 md:w-5/12 bg-gradient-to-br from-[#252b3d] to-[#1a1f2e] border-r border-white/5 flex flex-col justify-center relative overflow-hidden text-center">
                    <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-48 h-48 bg-primary/20 rounded-full blur-3xl opacity-50"></div>
                    
                    <div className="mb-8 flex justify-center">
                        <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-600 rounded-2xl shadow-lg shadow-amber-500/40 flex items-center justify-center transform transition-transform hover:scale-105">
                            <CrownIcon className="w-10 h-10 text-white fill-white/10" />
                        </div>
                    </div>
                    
                    <h2 className="text-3xl font-black mb-4 leading-tight">
                        {title}
                    </h2>
                    
                    <p className="text-gray-400 mb-10 leading-relaxed text-sm font-medium">
                        {message}
                    </p>

                    <div className="flex justify-center">
                        <ul className="space-y-4 mb-4 text-left inline-block">
                            {[
                                "Unlimited Classes",
                                "Unlimited Students",
                                "Full Report Templates",
                                "Export & Save Tools"
                            ].map((feature, idx) => (
                                <li key={idx} className="flex items-center gap-3 group px-1">
                                    <CheckCircleIcon className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                                    <span className="font-semibold text-sm leading-tight text-gray-200">{feature}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Right Side: Actions */}
                <div className="p-10 md:w-7/12 flex flex-col justify-center bg-[#1a1f2e]">
                    <div className="text-center mb-8">
                        <h3 className="text-xl font-bold text-white mb-1">Choose your upgrade</h3>
                        <p className="text-sm text-gray-500 italic">Secure payment via Stripe</p>
                    </div>

                    <div className="space-y-4">
                        {/* Annual Plan */}
                        <button
                            onClick={() => handleUpgrade('annual')}
                            disabled={isLoading}
                            className="group relative w-full p-6 rounded-3xl border-2 border-primary bg-primary/5 hover:bg-primary/10 transition-all text-left shadow-lg shadow-primary/5"
                        >
                            <div className="absolute -top-3 right-6 px-3 py-1 bg-primary text-white text-[10px] font-black rounded-full uppercase tracking-widest shadow-md">
                                Best Value
                            </div>
                            <div className="flex justify-between items-center">
                                <div>
                                    <div className="font-bold text-lg text-white mb-0.5">Annual Plan</div>
                                    <div className="text-xs text-gray-400 font-medium">Billed yearly</div>
                                    {!hasUsedTrial && (
                                        <div className="mt-1 text-[10px] font-bold text-amber-500 uppercase tracking-wide">
                                            14-Day Free Trial • No Credit Card
                                        </div>
                                    )}
                                </div>
                                <div className="text-right">
                                    <div className="text-2xl font-black text-white">$49.99</div>
                                    <div className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">/year</div>
                                </div>
                            </div>
                        </button>

                        {/* Monthly Plan */}
                        <button
                            onClick={() => handleUpgrade('monthly')}
                            disabled={isLoading}
                            className="group w-full p-6 rounded-3xl border-2 border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20 transition-all text-left"
                        >
                            <div className="flex justify-between items-center">
                                <div>
                                    <div className="font-bold text-lg text-white mb-0.5">Monthly Plan</div>
                                    <div className="text-xs text-gray-400 font-medium">Billed monthly</div>
                                    {!hasUsedTrial && (
                                        <div className="mt-1 text-[10px] font-bold text-amber-500 uppercase tracking-wide">
                                            14-Day Free Trial • No Credit Card
                                        </div>
                                    )}
                                </div>
                                <div className="text-right">
                                    <div className="text-2xl font-black text-white">$5.99</div>
                                    <div className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">/month</div>
                                </div>
                            </div>
                        </button>
                    </div>

                    {error && (
                        <div className="mt-6 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-xs text-red-500 text-center font-medium">
                            {error}
                        </div>
                    )}

                    <div className="mt-10 text-center">
                        <button 
                            onClick={onClose} 
                            className="text-sm font-bold text-gray-500 hover:text-gray-300 transition-colors py-2 px-4 rounded-xl hover:bg-white/5"
                        >
                            Maybe later
                        </button>
                    </div>

                    {isLoading && (
                        <div className="absolute inset-0 bg-[#1a1f2e]/80 backdrop-blur-[2px] flex items-center justify-center z-20">
                            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default UpgradeModal;
